import { useEffect, useMemo, useState } from "react";
import { IconSearch } from "@tabler/icons-react";
import { Card, Checkbox, Group, Input, PaginadorTabla, Select, Stack } from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import { useClavesSatAdmin } from "../hooks/use-claves-sat-admin";
import { listarClavesSatAdmin, type ClaveSatAdmin, type EstadoClaveSatFiltro } from "../services/claves-sat-admin-service";
import { ActualizacionCatalogoPanel } from "./ActualizacionCatalogoPanel";
import { CatalogosSatHeader } from "./CatalogosSatHeader";
import { ClaveSatCrearForm } from "./ClaveSatCrearForm";
import { ClavesSatTable } from "./ClavesSatTable";

const PAGE_SIZE = 15;

const OPCIONES_ESTADO = [
  { value: "todas", label: "Todas" },
  { value: "utilizables", label: "Vigentes hoy" },
  { value: "noUtilizables", label: "Descontinuadas o fuera de vigencia" },
];

/**
 * Página de administración del catálogo SAT · búsqueda, filtros y paginado se resuelven en el SERVIDOR
 * (el catálogo oficial tiene ~52 mil claves) vía GET /api/admin/claves-sat. Arriba, el panel para actualizar
 * el catálogo con el XLS del SAT. Los selectores del wizard buscan en /api/catalogos/claves-sat; las mutations
 * y el fin de una actualización invalidan ambas vistas.
 */
export function AdminCatalogosSatPage() {
  const [filtro, setFiltro] = useState("");
  const [estado, setEstado] = useState<EstadoClaveSatFiltro>("todas");
  const [soloFranja, setSoloFranja] = useState(false);
  const [modoCrear, setModoCrear] = useState(false);
  const [page, setPage] = useState(1);
  const [filtroDebounced] = useDebouncedValue(filtro, 350);

  const filtrosServer = useMemo(
    () => ({
      ...(filtroDebounced.trim() ? { busqueda: filtroDebounced.trim() } : {}),
      ...(estado !== "todas" ? { estado } : {}),
      ...(soloFranja ? { soloFranjaFronteriza: true } : {}),
    }),
    [filtroDebounced, estado, soloFranja]
  );

  useEffect(() => {
    setPage(1);
  }, [filtrosServer]);

  const { data } = useClavesSatAdmin({ ...filtrosServer, page, pageSize: PAGE_SIZE });
  const claves = data?.items ?? [];
  const total = data?.total ?? 0;

  const obtenerTodas = async (): Promise<ClaveSatAdmin[]> => {
    const todas: ClaveSatAdmin[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarClavesSatAdmin({ ...filtrosServer, page: p, pageSize: 100 });
      todas.push(...pagina.items);
      if (todas.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todas;
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <CatalogosSatHeader
        total={data?.totalCatalogo}
        totalUtilizables={data?.totalUtilizables}
        modoCrear={modoCrear}
        onIniciarCrear={() => setModoCrear(true)}
      />
      <Stack gap="md">
        <ActualizacionCatalogoPanel />
        {modoCrear ? <ClaveSatCrearForm onSalir={() => setModoCrear(false)} /> : null}
        <Card padding="lg">
          <Stack gap="md">
            <Group align="flex-end" gap="md" wrap="wrap">
              <Input
                label="Buscar"
                placeholder="clave, descripción o palabra similar…"
                leftSection={<IconSearch size={14} stroke={1.7} />}
                value={filtro}
                onChange={(e) => setFiltro(e.currentTarget.value)}
                wrapperClassName="min-w-70 flex-1"
              />
              <Select
                label="Estado"
                options={OPCIONES_ESTADO}
                value={estado}
                onValueChange={(v) => setEstado(v as EstadoClaveSatFiltro)}
                wrapperClassName="w-72"
              />
              <Checkbox label="Solo franja fronteriza" checked={soloFranja} onCheckedChange={(c) => setSoloFranja(c === true)} />
            </Group>
            <PaginadorTabla cargando={!data} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} variante="superior" />
            <ClavesSatTable cargando={!data} claves={claves} obtenerFilas={obtenerTodas} />
            <PaginadorTabla cargando={!data} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="claves" />
          </Stack>
        </Card>
      </Stack>
    </div>
  );
}
